import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SetState } from 'src/types/generic';
import { DefaultTransferListDataType } from './TransferList';
import Checkbox from './design-system/Checkbox';

interface Props<D> {
  items: D[];
  title: string;
  setItems: SetState<D[]>;
  height: string;
  filteredList: D[];
  deletedItemLabel?: string;
}

function List<D extends DefaultTransferListDataType>({
  items,
  setItems,
  title,
  height,
  filteredList,
  deletedItemLabel,
}: Props<D>) {
  const { t } = useTranslation();
  const isAllChecked = useMemo(
    () => !!filteredList.length && filteredList.every((item) => item.selected),
    [filteredList]
  );

  const handleSelectAllChange = useCallback(() => {
    const filteredListIds = filteredList?.map((el) => el?.id);

    setItems((prev) => [
      ...prev.map((item) => ({
        ...item,
        selected: filteredListIds.includes(item.id) ? !isAllChecked : item.selected,
      })),
    ]);
  }, [filteredList, isAllChecked, setItems]);

  const handleItemChange = useCallback(
    (item: D) => () => {
      setItems((prev) => [
        ...prev.map((prevItem) =>
          prevItem.id === item.id ? { ...item, selected: !item.selected } : prevItem
        ),
      ]);
    },
    [setItems]
  );

  const widthRef = useRef<HTMLDivElement | null>(null);

  const [maxWidth, setMaxWidth] = useState(0);

  useEffect(() => {
    if (widthRef.current) setMaxWidth(widthRef.current.offsetWidth);
  }, [widthRef]);

  return (
    <div className="flex flex-col flex-1 p-2 bg-[#00000006]">
      <div className="font-semibold text-sm px-4 py-2 bg-[#0000002e]" ref={widthRef}>
        <Checkbox
          checked={isAllChecked}
          onChange={handleSelectAllChange}
          label={`${t(title)} (${filteredList?.length})`}
        />
      </div>
      <ul className="p-4 overflow-auto" style={{ height, maxWidth: `${maxWidth}px` }}>
        {filteredList.map((item) => (
          <li
            key={item.rowId}
            className={`flex items-start justify-start cursor-pointer py-1 ${
              item.isDirty ? 'bg-warning bg-opacity-25' : ''
            }`}
            onClick={handleItemChange(item)}
          >
            <Checkbox
              checked={item.selected}
              onChange={handleItemChange(item)}
              label=""
              className="mt-1"
            />
            <div className="flex-grow text-sm">
              {item.name} <span className="text-xs">{item.isDeleted ? deletedItemLabel : ''}</span>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default List;
